
import React, { useState } from 'react';
import { Task, Quadrant, TaskStatus, UserProfile } from '../types';
import { Button } from '../components/Button';
import { Plus, ExternalLink, Zap, CheckCircle, ArrowRight } from 'lucide-react';
import { QUADRANT_COLORS, XP_VALUES } from '../constants';

declare const chrome: any;

interface PopupViewProps {
  user: UserProfile;
  tasks: Task[];
  onAddTask: (title: string, quadrant: Quadrant) => void;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
}

export const PopupView: React.FC<PopupViewProps> = ({ user, tasks, onAddTask, onUpdateStatus }) => {
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const activeTask = tasks.find(t => t.status === TaskStatus.Doing);
  const topQ1Tasks = tasks
    .filter(t => t.quadrant === Quadrant.Do && t.status === TaskStatus.Todo)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 3);

  const handleOpenDashboard = () => {
    if (typeof chrome !== 'undefined' && chrome.tabs) {
      chrome.tabs.create({ url: 'index.html' });
    } else {
      window.open('index.html', '_blank');
    }
  };

  const handleQuickAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    onAddTask(newTaskTitle, Quadrant.Do);
    setNewTaskTitle('');
  };

  const handleComplete = (taskId: string) => {
    onUpdateStatus(taskId, TaskStatus.Done);
  };

  return (
    <div className="w-[350px] min-h-[450px] bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-dark-text flex flex-col">
      <div className="bg-white dark:bg-dark-surface p-4 border-b border-gray-200 dark:border-dark-border flex items-center justify-between shadow-sm z-10">
        <div className="flex items-center gap-2">
           <img src="logo.png" className="w-6 h-6 rounded-md object-cover" alt="Logo" />
           <span className="font-bold text-sm">Eisenhower Pro</span>
        </div>
        <button 
          onClick={handleOpenDashboard}
          className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
          title="Open Dashboard"
        >
          <ExternalLink size={18} />
        </button>
      </div>

      <div className="flex-1 p-4 overflow-y-auto space-y-6">
        <div>
           <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
             Quick Capture (Do First)
           </label>
           <form onSubmit={handleQuickAdd} className="relative">
              <input 
                autoFocus
                type="text"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="What needs to be done?"
                className="w-full pl-3 pr-10 py-2.5 text-sm border border-gray-300 dark:border-dark-border dark:bg-gray-800 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button 
                type="submit"
                disabled={!newTaskTitle.trim()}
                className="absolute right-1.5 top-1.5 p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
              </button>
           </form>
        </div>

        {activeTask ? (
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-4 text-white shadow-lg shadow-blue-200 dark:shadow-none">
            <div className="flex items-center gap-2 mb-2 opacity-90">
               <Zap size={14} className="text-yellow-300 animate-pulse" />
               <span className="text-xs font-bold uppercase tracking-wider">Now Focusing</span>
            </div>
            <p className="font-semibold text-lg leading-tight mb-4">{activeTask.title}</p>
            <div className="flex gap-2">
               <Button 
                 size="sm" 
                 variant="secondary" 
                 className="flex-1 border-none bg-white/20 text-white hover:bg-white/30 text-xs"
                 onClick={handleOpenDashboard}
               >
                 Open Timer
               </Button>
               <Button 
                 size="sm" 
                 className="bg-white text-blue-700 hover:bg-blue-50 border-none flex-1 text-xs font-bold"
                 onClick={() => handleComplete(activeTask.id)}
               >
                 Complete
               </Button>
            </div>
          </div>
        ) : (
           <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-4 flex flex-col items-center justify-center text-center text-gray-400 dark:text-gray-500">
              <p className="text-sm">No active focus task.</p>
              <button 
                onClick={handleOpenDashboard} 
                className="text-blue-600 dark:text-blue-400 text-xs font-medium mt-1 hover:underline"
              >
                Go to Dashboard to pick one
              </button>
           </div>
        )}

        {topQ1Tasks.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-2">
               <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                 Up Next (Urgent)
               </label>
               <span className="text-[10px] bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded font-medium">
                 {topQ1Tasks.length}
               </span>
            </div>
            <div className="space-y-2">
               {topQ1Tasks.map(task => (
                 <div key={task.id} className="group flex items-center justify-between p-3 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-sm hover:shadow-md transition-all">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-1">{task.title}</span>
                    <button 
                      onClick={() => handleComplete(task.id)}
                      className="text-gray-300 hover:text-green-500 transition-colors opacity-0 group-hover:opacity-100"
                      title="Mark Done"
                    >
                       <CheckCircle size={18} />
                    </button>
                 </div>
               ))}
            </div>
          </div>
        )}

      </div>

      <div className="bg-gray-100 dark:bg-gray-800 p-2 text-center text-[10px] text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-dark-border">
         Lvl {user.level} {user.displayName ? `• ${user.displayName}` : ''}
      </div>
    </div>
  );
};
